/**
 * Simplified Owner Context for managing authentication headers.
 *
 * This context provides only the essential owner information needed
 * for API requests, focusing on headers management.
 */

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { clearCompanySpecificCaches } from '../utils/cacheManager'
import { config } from '../config'

// Simplified types - only what's needed for headers
export interface OwnerHeaders {
  company: string
  businessUnit: string
  logisticOperator: string
  user: string
  userToken?: string
  companyName?: string
  companyData?: any
}

export interface OwnerContextType {
  headers: OwnerHeaders | null
  isLoading: boolean
  error: string | null
  isOwnerValid: boolean
  initializeOwner: (dataParam: string) => Promise<void>
  refreshHeaders: () => boolean
  clearOwner: () => void
  switchCompany: (companyData: any) => Promise<void>
}

// Create context
const OwnerContext = createContext<OwnerContextType | undefined>(undefined)

// Provider component
interface OwnerProviderProps {
  children: ReactNode
}

export const OwnerProvider: React.FC<OwnerProviderProps> = ({ children }) => {
  const [headers, setHeaders] = useState<OwnerHeaders | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isOwnerValid, setIsOwnerValid] = useState(false)

  // Initialize owner from URL data parameter
  const initializeOwner = async (dataParam: string) => {
    try {
      setIsLoading(true)
      setError(null)

      // Helper function to fetch with timeout
      const fetchWithTimeout = async (url: string, options: RequestInit, timeout = 10000): Promise<Response> => {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), timeout)

        try {
          const response = await fetch(url, {
            ...options,
            signal: controller.signal
          })
          clearTimeout(timeoutId)
          return response
        } catch (error) {
          clearTimeout(timeoutId)
          if (error instanceof Error && error.name === 'AbortError') {
            throw new Error('Tempo limite de conexão excedido. Verifique sua conexão.')
          }
          throw error
        }
      }

      // Call backend to initialize owner - use configured API URL (auto-detected)
      // Note: This runs in the browser, so it needs the external URL (not container name)
      const response = await fetchWithTimeout(`${config.API_BASE_URL}/auth/initialize?data=${encodeURIComponent(dataParam)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }, 15000)

                   if (!response.ok) {
                       // Handle specific error responses
                       if (response.status === 401) {
                           const errorData = await response.json().catch(() => ({}))
                           if (errorData.detail?.error === 'TOKEN_INVALID_OR_EXPIRED') {
                               throw new Error('Token expirado. Faça login novamente no sistema principal.')
                           }
                       }
                       throw new Error(`HTTP error! status: ${response.status}`)
                   }

      const data = await response.json()

      if (data.success) {
        // NOVA ARQUITETURA SIMPLIFICADA: Usar apenas dados essenciais
        // Extract essential headers data from simplified token data
        const ownerHeaders: OwnerHeaders = {
          company: data.owner.company || '',
          businessUnit: data.owner.businessUnit || '-1',
          logisticOperator: data.owner.logisticOperator || '-1',
          user: data.owner.userName || '',
          userToken: data.owner.userToken,
          companyName: data.owner.companyName || '', // Essential name from token
          companyData: data.companyData || null // Essential company data from token
        }


        setHeaders(ownerHeaders)

        // Store in localStorage for API service
        const localStorageData = {
          company: ownerHeaders.company,
          businessUnit: ownerHeaders.businessUnit,
          logisticOperator: ownerHeaders.logisticOperator,
          userName: ownerHeaders.user,
          userToken: ownerHeaders.userToken,
          companyName: ownerHeaders.companyName, // Uses real company name
          companyData: ownerHeaders.companyData
        }
        
        localStorage.setItem('owner_data', JSON.stringify(localStorageData))

        // Mark owner as valid
        setIsOwnerValid(true)
        setError(null)

      } else {
        throw new Error(data.message || 'Failed to initialize owner')
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      setError(errorMessage)
      setIsOwnerValid(false)
    } finally {
      setIsLoading(false)
    }
  }

  // Refresh headers from localStorage
  const refreshHeaders = (): boolean => {
    try {
      setIsLoading(true)
      const storedData = localStorage.getItem('owner_data')
      if (storedData) {
        const owner = JSON.parse(storedData)
        // Validate that we have minimum required data
        if (owner.company && owner.userToken) {
          setHeaders({
            company: owner.company || '',
            businessUnit: owner.businessUnit || '-1',
            logisticOperator: owner.logisticOperator || '-1',
            user: owner.userName || '',
            userToken: owner.userToken,
            companyName: owner.companyName || '',
            companyData: owner.companyData || null
          })
          setIsOwnerValid(true)
          setError(null)
          return true
        } else {
          // Data exists but is incomplete
          setIsOwnerValid(false)
          setError('Dados de autenticação incompletos')
          return false
        }
      } else {
        setIsOwnerValid(false)
        setError(null) // Not an error, just no data - waiting for auth
        return false
      }
    } catch (err) {
      setIsOwnerValid(false)
      setError('Dados de autenticação inválidos')
      return false
    } finally {
      setIsLoading(false)
    }
  }

  // Switch to a different company
  const switchCompany = async (companyData: any) => {
    try {
      setIsLoading(true)
      setError(null)

      // Step 1: Get the old company (before switching)
      const oldCompany = headers?.company

      // Step 2: Create new owner headers with the new company data
      const newOwnerHeaders: OwnerHeaders = {
        company: companyData.documentNumber || companyData.cnpj || '',
        businessUnit: '-1',
        logisticOperator: '-1',
        user: headers?.user || '', // Keep current user
        userToken: headers?.userToken, // Keep current token
        companyName: companyData.name || '',
        companyData: companyData,
      }

      // Step 3: Clear analysis caches for the old company before switching
      if (oldCompany && oldCompany !== newOwnerHeaders.company) {
        console.log(`[OwnerContext] Switching from company ${oldCompany} to ${newOwnerHeaders.company}`)
        clearCompanySpecificCaches(oldCompany)
        console.log('[OwnerContext] Old company caches cleared')
      }

      setHeaders(newOwnerHeaders)

      // Step 4: Update localStorage with new company data
      const localStorageData = {
        company: newOwnerHeaders.company,
        businessUnit: newOwnerHeaders.businessUnit,
        logisticOperator: newOwnerHeaders.logisticOperator,
        userName: newOwnerHeaders.user,
        userToken: newOwnerHeaders.userToken,
        companyName: newOwnerHeaders.companyName,
        companyData: newOwnerHeaders.companyData,
      }

      localStorage.setItem('owner_data', JSON.stringify(localStorageData))

      // Mark owner as valid
      setIsOwnerValid(true)
      setError(null)

      console.log('[OwnerContext] Company switched successfully')
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      setError(errorMessage)
      setIsOwnerValid(false)
      console.error('[OwnerContext] Company switch error:', errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  // Clear owner data completely
  const clearOwner = () => {
    
    // Reset all state
    setHeaders(null)
    setIsOwnerValid(false)
    setError(null)
    setIsLoading(false)
    
    // Clear all possible owner-related data from localStorage
    const localStorageKeys = [
      'owner_data',
      'lognex_ia_owner', 
      'company_data',
      'business_units',
      'user_token',
      'auth_token',
      'lognex_owner',
      'user_data',
      'company_info',
      'auth_data'
    ]
    
    localStorageKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key)
      }
    })
    
    // Clear all possible owner-related data from sessionStorage
    const sessionStorageKeys = [
      'owner_data',
      'lognex_ia_owner',
      'company_data', 
      'business_units',
      'user_token',
      'auth_token',
      'lognex_owner',
      'user_data',
      'company_info',
      'auth_data'
    ]
    
    sessionStorageKeys.forEach(key => {
      if (sessionStorage.getItem(key)) {
        sessionStorage.removeItem(key)
      }
    })
    
    // Clear any cookies related to authentication
    document.cookie.split(";").forEach(function(cookie) { 
      const eqPos = cookie.indexOf("=")
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim()
      
      // Clear cookies that might contain owner/auth data
      if (name.includes('owner') || name.includes('auth') || name.includes('token') || name.includes('user')) {
        document.cookie = `${name}=;expires=${new Date(0).toUTCString()};path=/`
        document.cookie = `${name}=;expires=${new Date(0).toUTCString()};path=/;domain=${window.location.hostname}`
        document.cookie = `${name}=;expires=${new Date(0).toUTCString()};path=/;domain=.${window.location.hostname}`
      }
    })
    
  }

  // Initialize from URL on mount - runs only once
  useEffect(() => {
    let mounted = true

    const initialize = async () => {
      const urlParams = new URLSearchParams(window.location.search)
      const dataParam = urlParams.get('data')

      if (dataParam) {
        // Has data param in URL - initialize from it
        try {
          await initializeOwner(dataParam)
          if (mounted) {
            // Clear the data parameter from URL after successful initialization
            const newUrl = window.location.pathname
            window.history.replaceState({}, '', newUrl)
          }
        } catch (err) {
          // Error already handled by initializeOwner
          console.error('[OwnerContext] Initialization error:', err)
        }
      } else {
        // No data param - try to load from localStorage
        refreshHeaders()
      }
    }

    initialize()

    return () => {
      mounted = false
    }
  }, []) // Empty dependency array - run only on mount

  // Listen for URL changes (when company is switched in lognex-core-frontend)
  useEffect(() => {
    let lastUrl = window.location.href
    let isProcessing = false

    const handleUrlChange = async () => {
      if (isProcessing) return // Prevent concurrent processing

      const urlParams = new URLSearchParams(window.location.search)
      const dataParam = urlParams.get('data')

      if (dataParam) {
        isProcessing = true
        try {
          await initializeOwner(dataParam)
          // Clear the data parameter from URL after successful initialization
          const newUrl = window.location.pathname
          window.history.replaceState({}, '', newUrl)
          lastUrl = window.location.href
        } catch (err) {
          // Error already handled by initializeOwner
          console.error('[OwnerContext] URL change initialization error:', err)
        } finally {
          isProcessing = false
        }
      }
    }

    // Poll for URL changes (more reliable than event listeners)
    const urlCheckInterval = setInterval(() => {
      if (window.location.href !== lastUrl && !isProcessing) {
        lastUrl = window.location.href
        handleUrlChange()
      }
    }, 500) // Check every 500ms for faster response

    // Also listen for events as backup
    window.addEventListener('popstate', handleUrlChange)
    window.addEventListener('hashchange', handleUrlChange)

    // Cleanup listeners
    return () => {
      clearInterval(urlCheckInterval)
      window.removeEventListener('popstate', handleUrlChange)
      window.removeEventListener('hashchange', handleUrlChange)
    }
  }, []) // Empty dependency - setup once

  const contextValue: OwnerContextType = {
    headers,
    isLoading,
    error,
    isOwnerValid,
    initializeOwner,
    refreshHeaders,
    clearOwner,
    switchCompany
  }

  return (
    <OwnerContext.Provider value={contextValue}>
      {children}
    </OwnerContext.Provider>
  )
}

// Hook to use owner context
export const useOwner = (): OwnerContextType => {
  const context = useContext(OwnerContext)
  if (context === undefined) {
    throw new Error('useOwner must be used within an OwnerProvider')
  }
  return context
}

// Helper hook for headers only
export const useOwnerHeaders = (): OwnerHeaders | null => {
  const { headers } = useOwner()
  return headers
}